// Buffers anonymous web events and flushes them to /web-events, but only once
// the session shows a human engagement signal. Sessions that never engage
// (crawlers, headless scrapers, instant bounces) are never sent, so the
// overwhelming majority of bot traffic never reaches the database.
//
// Engagement = continuous visible dwell past a threshold, OR a *trusted*
// interaction (scroll/pointer/keyboard). Synthetic events from automation have
// event.isTrusted === false and do not count. Emitting an event that itself
// requires a human action (a click, a dismiss) also opens the gate.
//
// Uses fetch(keepalive) so events survive page unload AND can carry the CSRF
// header (required: CSRF is the server-side security gate for this endpoint).

const ENDPOINT = "/web-events"

// Continuous visible time that we treat as "a person is actually reading".
const ENGAGEMENT_DWELL_MS = 5000

// Trusted DOM events that prove a human is driving the page.
const INTERACTION_EVENTS = [ "scroll", "pointerdown", "keydown" ]

// Tracked events that, by their nature, require a human to have acted. Emitting
// one proves engagement and unlocks the flush gate for the rest of the session.
// (web_session_started and app_drawer_shown are passive and excluded.)
const ENGAGEMENT_EVENTS = new Set([
  "signup_wall_shown",
  "signup_wall_dismissed",
  "app_drawer_web_click",
  "app_drawer_app_click",
  "app_redirect_click",
  "web_signup_click",
  "match_teaser_click",
  "premium_paywall_modal_dismissed"
])

const queue = []
let flushTimer = null
let dwellTimer = null
let sessionEngaged = false
let sessionStartedAt = null
let interactionHandler = null

function webAnalyticsEnabled() {
  return document.querySelector('meta[name="web-analytics-enabled"]')
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

function flush() {
  if (!sessionEngaged) return // gate: never persist a session with no human signal
  if (queue.length === 0) return
  const token = csrfToken()
  if (!token) return // no token => not a real rendered page; skip

  const events = queue.splice(0, queue.length)
  fetch(ENDPOINT, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": token
    },
    body: JSON.stringify({ events })
  }).catch(() => {
    // Swallow errors; analytics must never disrupt UX.
  })
}

function scheduleFlush() {
  clearTimeout(flushTimer)
  flushTimer = setTimeout(flush, 1500) // small debounce/batch
}

// First proof of a human: record how/when it happened on the buffered
// web_session_started, open the gate, and flush everything collected so far.
function markEngaged(via) {
  if (sessionEngaged) return
  sessionEngaged = true
  clearTimeout(dwellTimer)
  removeInteractionListeners()

  const started = queue.find((event) => event.name === "web_session_started")
  if (started) {
    started.properties.engaged_via = via
    started.properties.dwell_ms = sessionStartedAt ? Date.now() - sessionStartedAt : 0
  }

  flush()
}

export function trackWebEvent(name, properties = {}) {
  if (!webAnalyticsEnabled()) return

  queue.push({
    name,
    occurred_at: Date.now(), // ms epoch; server converts
    properties
  })

  if (ENGAGEMENT_EVENTS.has(name)) {
    markEngaged("action")
  } else {
    scheduleFlush()
  }
}

// One web_session_started per browser session. It is buffered, not sent, until
// the session engages, so it counts engaged sessions (the meaningful denominator).
function trackSessionStart() {
  if (!webAnalyticsEnabled()) return
  if (sessionStorage.getItem("web_session_started")) return

  sessionStorage.setItem("web_session_started", "1")
  sessionStartedAt = Date.now()
  trackWebEvent("web_session_started", { path: window.location.pathname })
  armEngagement()
}

function armEngagement() {
  startDwell()

  interactionHandler = (event) => {
    if (event.isTrusted) markEngaged(event.type)
  }
  INTERACTION_EVENTS.forEach((type) => {
    document.addEventListener(type, interactionHandler, { passive: true, capture: true })
  })
}

function removeInteractionListeners() {
  if (!interactionHandler) return
  INTERACTION_EVENTS.forEach((type) => {
    document.removeEventListener(type, interactionHandler, { capture: true })
  })
  interactionHandler = null
}

// Requires the page to be continuously visible; resets if backgrounded. This
// naturally excludes prerenders/background preloads, which never become visible.
function startDwell() {
  clearTimeout(dwellTimer)
  if (document.visibilityState !== "visible") return
  dwellTimer = setTimeout(() => markEngaged("dwell"), ENGAGEMENT_DWELL_MS)
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    clearTimeout(dwellTimer) // pause the dwell countdown while backgrounded
    flush() // no-op unless already engaged (gate lives in flush)
  } else if (!sessionEngaged) {
    startDwell() // resume the dwell countdown when visible again
  }
})
window.addEventListener("pagehide", flush)

document.addEventListener("turbo:load", trackSessionStart)
